import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Dialog,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Divider,
  useTheme,
} from "@mui/material";
import {
  LuSearch,
  LuX,
  LuStar,
  LuPlus,
} from "react-icons/lu";
import { getOrgTokens, ORG_PALETTE } from "../../theme/style/organizationPalette";
import { Env_Factor_Options } from "../../db_access/env_factor_options";
import AgGrid from "../grid/AgGrid";
import {
  centerAlignColDef,
  leftAlignColDef,
  rightAlignColDef,
} from "../grid/columnDefinitions/columnDefinitions";
import CloseButton from "../button/CloseButton";

const FAVORITES_STORAGE_KEY = "arivu_epd_favorites";
const USER_DEFINED_STORAGE_KEY = "arivu_user_defined_materials";



export default function EPDLibraryDialog({
  open,
  onClose,
  onSelect,
  supplierData = [],
  mode = "add",
  initialSearch = "",
  currentProductId = null,
  currentProjectId = null,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const gridApiRef = useRef(null);

  // Search & Navigation State
  const [searchTerm, setSearchTerm] = useState(initialSearch || "");
  const [activeTab, setActiveTab] = useState("all");

  // Data State
  const [remoteResults, setRemoteResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedGridRows, setSelectedGridRows] = useState([]);

  // User Defined inline creator state
  const [isCreatingUserDefined, setIsCreatingUserDefined] = useState(false);
  const [newUserMaterial, setNewUserMaterial] = useState({
    description: "",
    category: "General",
    sub_category: "Custom",
    unit: "kg",
    gwp_per_unit: "",
    biogenic_gwp_per_unit: "",
    density_kg_m3: "",
  });

  // Favorites persistence
  const [favorites, setFavorites] = useState(() => {
    try {
      const stored = localStorage.getItem(FAVORITES_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // User Defined items persistence
  const [userDefinedItems, setUserDefinedItems] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_DEFINED_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const getItemUniqueKey = useCallback((item) => {
    if (!item) return "";
    return `${item.source_type}_${item.id || item.description || item.display_name}`;
  }, []);

  const toggleFavorite = useCallback((itemKey) => {
    setFavorites((prev) => {
      const next = prev.includes(itemKey)
        ? prev.filter((k) => k !== itemKey)
        : [...prev, itemKey];
      try {
        localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(next));
      } catch (e) {
        console.warn("Error saving favorites:", e);
      }
      return next;
    });
    if (gridApiRef.current) {
      gridApiRef.current.refreshCells({ force: true });
    }
  }, []);

  // Fetch remote materials
  const fetchRemoteData = useCallback(async (query) => {
    setIsLoading(true);
    try {
      let response;
      if (query && query.trim().length >= 2) {
        response = await Env_Factor_Options.searchMaterials(query.trim(), true);
      } else {
        response = await Env_Factor_Options.browseAll({ limit: 300 });
      }
      const data = await response.json();
      setRemoteResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching EPD library materials:", err);
      setRemoteResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearchTerm(initialSearch || "");
      fetchRemoteData(initialSearch || "");
      setSelectedGridRows([]);
    }
  }, [open, initialSearch, fetchRemoteData]);

  // Handle Search typing with debounce
  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => {
      fetchRemoteData(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, open, fetchRemoteData]);

  // Format supplierData items (excluding current product/project to prevent self-referential cycle)
  const formattedSupplierItems = useMemo(() => {
    if (!Array.isArray(supplierData)) return [];
    return supplierData
      .filter((s) => {
        if (currentProductId && (String(s.id) === String(currentProductId) || String(s.product_id) === String(currentProductId))) {
          return false;
        }
        if (currentProjectId && (String(s.id) === String(currentProjectId) || String(s.project_id) === String(currentProjectId))) {
          return false;
        }
        return true;
      })
      .map((s) => ({
        id: s.id || `supp_${s.supplier}_${s.product || s.name}`,
        source_type: "supplier",
        type: "Supplier Product",
        category: s.category || "Supplier Materials",
        sub_category: s.sub_category || "Catalog Item",
        description: s.product ? `${s.supplier}_${s.product}` : s.name || s.supplier,
        display_name: s.product || s.name || s.supplier,
        manufacturer: s.supplier || "Connected Supplier",
        unit: s.functional_unit || "kg",
        default_unit: s.functional_unit || "kg",
        gwp_per_unit: s.gwp_total !== undefined ? s.gwp_total : s.gwp || 0,
        geography: s.place_of_manufacture || "Local / Supplier",
        year: s.Year || s.year || 2024,
        gwp_reference: s.epd_link || "Supplier Directory",
        note: s.specifications || s.product_intro || "",
      }));
  }, [supplierData, currentProductId, currentProjectId]);

  // Normalized pool of resources
  const allResources = useMemo(() => {
    const list = [];

    // 1. Remote items (north_american_epd & env_factor_options)
    remoteResults.forEach((r) => {
      const isEPD = r.source_type === "north_american_epd";
      list.push({
        ...r,
        id: r.id || `${r.source_type}_${r.description || r.name}`,
        source_type: isEPD ? "north_american_epd" : "env_factor_options",
        type: isEPD ? "North American EPD" : r.type || "Env Factor",
        category: r.category || (isEPD ? "EPD" : "General"),
        sub_category: r.sub_category || (isEPD ? "North America" : "Standard"),
        display_name: r.name || r.description,
        description: r.description || r.name,
        manufacturer: r.manufacturer || (isEPD ? "Verified EPD Program" : "Standard Database"),
        unit: r.unit || "kg",
        default_unit: r.unit || "kg",
        gwp_per_unit:
          r.gwp_per_unit !== undefined
            ? parseFloat(r.gwp_per_unit)
            : r.gwp !== undefined
            ? parseFloat(r.gwp)
            : 0,
        geography: r.geography || (isEPD ? "USA" : "Global"),
      });
    });

    // 2. Supplier items
    formattedSupplierItems.forEach((s) => list.push(s));

    // 3. User Defined items
    userDefinedItems.forEach((u) => {
      list.push({
        ...u,
        id: u.id || `user_${u.description}`,
        source_type: "user_defined",
        type: "User Defined",
        category: u.category || "User Entry",
        sub_category: u.sub_category || "Custom",
        display_name: u.description,
        manufacturer: "Custom Engineering",
        unit: u.unit || "kg",
        default_unit: u.unit || "kg",
      });
    });

    return list;
  }, [remoteResults, formattedSupplierItems, userDefinedItems]);

  // Filter helper for local items (supplier & user_defined) when searching
  const matchesSearchTerm = useCallback((item, term) => {
    if (!term || term.trim().length < 2) return true;
    const q = term.trim().toLowerCase();
    return (
      (item.display_name || "").toLowerCase().includes(q) ||
      (item.description || "").toLowerCase().includes(q) ||
      (item.manufacturer || "").toLowerCase().includes(q) ||
      (item.category || "").toLowerCase().includes(q) ||
      (item.sub_category || "").toLowerCase().includes(q)
    );
  }, []);

  // Filtered resources based on Active Tab & Search Term for local items
  const filteredResources = useMemo(() => {
    return allResources
      .filter((item) => {
        // 1. Search term filter for local items (supplier & user_defined)
        if (item.source_type === "supplier" || item.source_type === "user_defined") {
          if (!matchesSearchTerm(item, searchTerm)) return false;
        }

        const key = getItemUniqueKey(item);

        if (activeTab === "favorites" && !favorites.includes(key)) return false;
        if (activeTab === "north_american" && item.source_type !== "north_american_epd")
          return false;
        if (activeTab === "supplier" && item.source_type !== "supplier") return false;
        if (activeTab === "user_defined" && item.source_type !== "user_defined") return false;

        return true;
      })
      .map((item) => {
        const key = getItemUniqueKey(item);
        return {
          ...item,
          is_favorite: favorites.includes(key),
        };
      });
  }, [allResources, activeTab, favorites, getItemUniqueKey, searchTerm, matchesSearchTerm]);

  // Tab counts
  const tabCounts = useMemo(() => {
    const counts = {
      all: 0,
      favorites: 0,
      north_american: 0,
      supplier: 0,
      user_defined: 0,
    };

    allResources.forEach((item) => {
      if (item.source_type === "supplier" || item.source_type === "user_defined") {
        if (!matchesSearchTerm(item, searchTerm)) return;
      }

      counts.all += 1;
      const key = getItemUniqueKey(item);
      if (favorites.includes(key)) counts.favorites += 1;
      if (item.source_type === "north_american_epd") counts.north_american += 1;
      if (item.source_type === "supplier") counts.supplier += 1;
      if (item.source_type === "user_defined") counts.user_defined += 1;
    });

    return counts;
  }, [allResources, favorites, getItemUniqueKey, searchTerm, matchesSearchTerm]);

  const handleSelectSingle = (item) => {
    if (onSelect) {
      onSelect(item);
    }
    onClose();
  };

  const handleAddSelectedBatch = () => {
    if (selectedGridRows.length === 0) return;
    if (onSelect) {
      onSelect(selectedGridRows);
    }
    onClose();
  };

  const handleCreateUserDefined = () => {
    if (!newUserMaterial.description.trim()) return;
    const gwpVal = parseFloat(newUserMaterial.gwp_per_unit);
    const bioVal = parseFloat(newUserMaterial.biogenic_gwp_per_unit);
    const newItem = {
      id: `user_def_${Date.now()}`,
      source_type: "user_defined",
      type: "User Defined",
      description: newUserMaterial.description.trim(),
      display_name: newUserMaterial.description.trim(),
      category: newUserMaterial.category.trim() || "User Entry",
      sub_category: newUserMaterial.sub_category.trim() || "Custom",
      unit: newUserMaterial.unit || "kg",
      default_unit: newUserMaterial.unit || "kg",
      gwp_per_unit: !isNaN(gwpVal) ? gwpVal : 0,
      biogenic_gwp_per_unit: !isNaN(bioVal) ? bioVal : 0,
      gwp_unit: !isNaN(gwpVal) ? gwpVal : 0,
      biogenic_gwp: !isNaN(bioVal) ? bioVal : 0,
      default_gwp: !isNaN(gwpVal) ? gwpVal : 0,
      default_biogenic_gwp: !isNaN(bioVal) ? bioVal : 0,
      density_kg_m3: newUserMaterial.density_kg_m3
        ? parseFloat(newUserMaterial.density_kg_m3)
        : null,
      geography: "Custom",
      year: new Date().getFullYear(),
    };

    const updated = [newItem, ...userDefinedItems];
    setUserDefinedItems(updated);
    try {
      localStorage.setItem(USER_DEFINED_STORAGE_KEY, JSON.stringify(updated));
    } catch (e) {
      console.warn("Failed saving user defined material:", e);
    }

    setIsCreatingUserDefined(false);
    setNewUserMaterial({
      description: "",
      category: "General",
      sub_category: "Custom",
      unit: "kg",
      gwp_per_unit: "",
      biogenic_gwp_per_unit: "",
      density_kg_m3: "",
    });

    handleSelectSingle(newItem);
  };

  // AgGrid Column Definitions adhering to AGENTS.md rules & tok.gridCellText
  const columnDefs = useMemo(() => {
    return [
      // 1. Checkbox Column (No header checkbox, 24px left breathing room from border)
      {
        headerName: " ",
        field: "_checkbox",
        width: 68,
        minWidth: 68,
        maxWidth: 68,
        pinned: "left",
        checkboxSelection: true,
        headerCheckboxSelection: false,
        headerCheckboxSelectionFilteredOnly: false,
        suppressMovable: true,
        sortable: false,
        filter: false,
        resizable: false,
        headerClass: "epd-checkbox-header",
        cellClass: "epd-checkbox-cell",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          paddingLeft: "24px",
        },
      },
      // 2. Resource Name (Strict Left Alignment, Material Search Font Sizes & Colors)
      {
        headerName: "Resource Name",
        field: "display_name",
        minWidth: 340,
        flex: 2,
        headerClass: "epd-resource-header",
        cellClass: "epd-resource-cell",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          textAlign: "left",
          paddingLeft: "16px",
        },
        cellRenderer: (params) => {
          const item = params.data;
          if (!item) return null;
          const text = item.display_name || item.description || "";

          // Colorful breadcrumb tags matching Image 1
          const parts = [];
          if (item.type && item.type !== "Env Factor" && item.type !== "Standard") {
            parts.push({ label: item.type, color: isDark ? "#38bdf8" : "#0284c7" });
          } else if (item.source_type === "north_american_epd") {
            parts.push({ label: "North American EPD", color: isDark ? "#38bdf8" : "#0284c7" });
          } else if (item.type) {
            parts.push({ label: item.type, color: isDark ? "#38bdf8" : "#0284c7" });
          }

          if (item.category && item.category !== "General") {
            parts.push({ label: item.category, color: isDark ? "#4ade80" : "#16a34a" });
          }

          if (item.sub_category && item.sub_category !== "Standard" && item.sub_category !== "Custom") {
            parts.push({ label: item.sub_category, color: isDark ? "#fb923c" : "#ea580c" });
          }

          return (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                textAlign: "left",
                py: 0.5,
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  fontFamily: ORG_PALETTE.font.family,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: isDark ? "#ffffff" : "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  textAlign: "left",
                  lineHeight: 1.35,
                }}
                title={text}
              >
                {text}
              </Typography>
              {parts.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    flexWrap: "nowrap",
                    overflow: "hidden",
                    mt: 0.25,
                  }}
                >
                  {parts.map((p, idx) => (
                    <React.Fragment key={idx}>
                      {idx > 0 && (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "12px",
                            color: isDark ? "#64748b" : "#94a3b8",
                            userSelect: "none",
                            lineHeight: 1,
                          }}
                        >
                          →
                        </Typography>
                      )}
                      <Typography
                        component="span"
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: p.color,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          lineHeight: 1.2,
                        }}
                      >
                        {p.label}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              )}
            </Box>
          );
        },
      },
      // 3. Unit (Order: Resource Name -> Unit -> GWP -> Region -> Supplier/Manufacturer)
      {
        headerName: "Unit",
        field: "unit",
        width: 85,
        ...centerAlignColDef,
        cellRenderer: (params) => {
          return (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: isDark ? "#e2e8f0" : "#1e293b",
                fontFamily: ORG_PALETTE.font.family,
              }}
            >
              {params.value || "kg"}
            </Typography>
          );
        },
      },
      // 4. GWP (kg CO₂e)
      {
        headerName: "GWP (kg CO₂e)",
        field: "gwp_per_unit",
        width: 155,
        minWidth: 155,
        ...rightAlignColDef,
        cellRenderer: (params) => {
          const val = params.value;
          const formatted =
            val != null && !isNaN(val)
              ? Number(val).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                })
              : "0.00";
          return (
            <Typography
              sx={{
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "15px",
                fontWeight: 600,
                color: isDark ? "#ffffff" : "#0f172a",
              }}
            >
              {formatted}
            </Typography>
          );
        },
      },
      // 5. Region
      {
        headerName: "Region",
        field: "geography",
        width: 130,
        ...centerAlignColDef,
        cellRenderer: (params) => {
          const geo = params.value || "Global";
          return (
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: isDark ? "#e2e8f0" : "#1e293b",
                fontFamily: ORG_PALETTE.font.family,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={geo}
            >
              {geo}
            </Typography>
          );
        },
      },
      // 6. Supplier / Manufacturer (Strict Left Alignment)
      {
        headerName: "Supplier / Manufacturer",
        field: "manufacturer",
        minWidth: 200,
        flex: 1.2,
        headerClass: "epd-supplier-header",
        cellClass: "epd-supplier-cell",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          textAlign: "left",
          paddingLeft: "16px",
        },
        cellRenderer: (params) => {
          return (
            <Typography
              sx={{
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "14px",
                fontWeight: 500,
                color: isDark ? "#e2e8f0" : "#1e293b",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
              }}
              title={params.value || "—"}
            >
              {params.value || "—"}
            </Typography>
          );
        },
      },
      // 7. Favorites ⭐
      {
        headerName: "⭐",
        field: "is_favorite",
        width: 52,
        maxWidth: 52,
        pinned: "right",
        ...centerAlignColDef,
        cellRenderer: (params) => {
          const item = params.data;
          if (!item) return null;
          const key = getItemUniqueKey(item);
          const isFav = params.value === true || favorites.includes(key);
          return (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const nextState = !isFav;
                if (params.node) {
                  params.node.setDataValue("is_favorite", nextState);
                }
                toggleFavorite(key);
              }}
              sx={{
                p: 0.5,
                color: isFav ? "#eab308" : isDark ? "#475569" : "#cbd5e1",
                "&:hover": { color: "#eab308" },
              }}
            >
              <LuStar
                size={19}
                fill={isFav ? "#eab308" : "transparent"}
                stroke={isFav ? "#eab308" : isDark ? "#94a3b8" : "#94a3b8"}
              />
            </IconButton>
          );
        },
      },
    ];
  }, [
    favorites,
    isDark,
    tok,
    getItemUniqueKey,
    toggleFavorite,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: tok.bg.card || (isDark ? "#18181b" : "#ffffff"),
          backgroundImage: "none",
          display: "flex",
          flexDirection: "column",
          borderRadius: ORG_PALETTE.radius.card || "14px",
          height: "84vh",
          maxHeight: "880px",
          width: "95vw",
          maxWidth: "1360px",
          border: `1px solid ${tok.border.card || (isDark ? "#52525b" : "#c8d4e0")}`,
          boxShadow: isDark ? "0 24px 48px rgba(0,0,0,0.7)" : "0 24px 48px rgba(0,0,0,0.12)",
          overflow: "hidden",
        },
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${tok.border.divider || (isDark ? "#52525b" : "#c8d4e0")}`,
          bgcolor: tok.bg.card,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{
              fontFamily: ORG_PALETTE.font.family,
              fontSize: ORG_PALETTE.font.size.tableTitle || "20px",
              fontWeight: 600,
              color: tok.text.tableTitle || (isDark ? "#ffffff" : "#0f172a"),
            }}
          >
            Resource Library
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LuPlus size={15} />}
            onClick={() => setIsCreatingUserDefined(true)}
            sx={{
              height: "32px",
              textTransform: "none",
              fontFamily: ORG_PALETTE.font.family,
              fontSize: "12px",
              fontWeight: 600,
              borderRadius: ORG_PALETTE.radius.input || "8px",
              borderColor: tok.bg.tabActive || "#659d33",
              color: tok.bg.tabActive || "#659d33",
              "&:hover": {
                bgcolor: isDark ? "rgba(101, 157, 51, 0.15)" : "rgba(101, 157, 51, 0.08)",
                borderColor: tok.bg.tabActive || "#659d33",
              },
            }}
          >
            Custom Material
          </Button>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              color: isDark ? "#94a3b8" : "#64748b",
              "&:hover": { color: isDark ? "#ffffff" : "#0f172a" },
            }}
          >
            <LuX size={18} />
          </IconButton>
        </Box>
      </Box>

      {/* ── SEARCH BAR ── */}
      <Box
        sx={{
          px: 3,
          pt: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          bgcolor: tok.bg.card,
        }}
      >
        <TextField
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by material (e.g. concrete, timber, steel)..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LuSearch size={18} color={isDark ? "#94a3b8" : "#64748b"} />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <LuX size={15} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "40px",
              borderRadius: ORG_PALETTE.radius.input || "8px",
              fontFamily: ORG_PALETTE.font.family,
              fontSize: "14px",
              bgcolor: isDark ? "rgba(255,255,255,0.03)" : "#ffffff",
              "& fieldset": {
                borderColor: tok.border.card || (isDark ? "#52525b" : "#cbd5e1"),
              },
            },
          }}
        />

        <Button
          variant="contained"
          onClick={() => fetchRemoteData(searchTerm)}
          sx={{
            height: "40px",
            px: 3,
            textTransform: "none",
            fontFamily: ORG_PALETTE.font.family,
            fontSize: "13px",
            fontWeight: 600,
            borderRadius: ORG_PALETTE.radius.input || "8px",
            bgcolor: tok.bg.tabActive || "#659d33",
            color: "#ffffff",
            boxShadow: "none",
            "&:hover": { bgcolor: tok.bg.tabActive || "#659d33", opacity: 0.9 },
          }}
        >
          Search
        </Button>
      </Box>

      {/* ── 5 SOURCE TABS (Segmented Pill Style) ── */}
      <Box
        sx={{
          px: 3,
          pb: 1.5,
          bgcolor: tok.bg.card,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${tok.border.divider || (isDark ? "#52525b" : "#e2e8f0")}`,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            bgcolor: tok.bg.tabContainer || (isDark ? "#27272a" : "#f1f5f9"),
            p: "4px",
            borderRadius: ORG_PALETTE.radius.tab || "12px",
          }}
        >
          {[
            { id: "all", label: "Arivu Database (All)", count: tabCounts.all },
            { id: "favorites", label: "Favourites ⭐", count: tabCounts.favorites },
            { id: "north_american", label: "North American EPDs", count: tabCounts.north_american },
            { id: "supplier", label: "Supplier Products", count: tabCounts.supplier },
            { id: "user_defined", label: "User Defined", count: tabCounts.user_defined },
          ].map((tab) => {
            const isSelected = activeTab === tab.id;
            const isSearching = Boolean(searchTerm && searchTerm.trim().length > 0);
            return (
              <Button
                key={tab.id}
                variant="text"
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  px: 1.75,
                  py: "5px",
                  borderRadius: ORG_PALETTE.radius.tabItem || "10px",
                  textTransform: "none",
                  fontSize: ORG_PALETTE.font.size.tabLabel || "14px",
                  fontWeight: 500,
                  fontFamily: ORG_PALETTE.font.family,
                  color: isSelected
                    ? "#ffffff"
                    : tok.text.tabInactive || (isDark ? "#94a3b8" : "#475569"),
                  bgcolor: isSelected ? tok.bg.tabActive || "#659d33" : "transparent",
                  boxShadow: isSelected ? tok.shadow.tabActive : "none",
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: isSelected
                      ? tok.bg.tabActive || "#659d33"
                      : isDark
                      ? "rgba(255,255,255,0.06)"
                      : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <span>{tab.label}</span>
                {isSearching && (
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "11.5px",
                      fontWeight: 500,
                      px: "6px",
                      py: "1px",
                      borderRadius: "12px",
                      bgcolor: isSelected
                        ? "rgba(255, 255, 255, 0.25)"
                        : isDark
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.06)",
                      color: isSelected ? "#ffffff" : isDark ? "#cbd5e1" : "#475569",
                    }}
                  >
                    {tab.count}
                  </Typography>
                )}
              </Button>
            );
          })}
        </Box>
      </Box>

      {/* ── AGGRID DATA TABLE (Seamless Edge-to-Edge Grid, no nested container) ── */}
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: tok.bg.card }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 2,
            }}
          >
            <CircularProgress size={36} sx={{ color: tok.bg.tabActive || "#659d33" }} />
            <Typography
              sx={{
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "14px",
                color: isDark ? "#94a3b8" : "#64748b",
              }}
            >
              Loading resources...
            </Typography>
          </Box>
        ) : filteredResources.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              gap: 1.5,
              p: 4,
              textAlign: "center",
            }}
          >
            <Typography
              sx={{
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "16px",
                fontWeight: 700,
                color: isDark ? "#ffffff" : "#0f172a",
              }}
            >
              No matching resources found
            </Typography>
            <Typography
              sx={{
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "13px",
                color: isDark ? "#94a3b8" : "#64748b",
                maxWidth: 450,
              }}
            >
              Try adjusting your search terms or create a custom user-defined material.
            </Typography>
          </Box>
        ) : (
          <AgGrid
            rowData={filteredResources}
            columnDefs={columnDefs}
            rowSelection="multiple"
            suppressRowClickSelection={true}
            rowHeight={64}
            headerHeight={44}
            onGridReady={(params) => {
              gridApiRef.current = params.api;
            }}
            onRowDoubleClicked={(params) => handleSelectSingle(params.data)}
            onSelectionChanged={(params) => {
              const selected = params.api.getSelectedRows();
              setSelectedGridRows(selected);
            }}
            pagination={true}
            paginationPageSize={10}
            domLayout="normal"
            sx={{
              height: "100%",
              width: "100%",
              // Enhanced row separation & gap:
              "& .ag-row": {
                borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"} !important`,
                transition: "background-color 0.15s ease",
              },
              "& .ag-row:hover": {
                bgcolor: isDark ? "rgba(255,255,255,0.04) !important" : "#f8fafc !important",
              },
              // SQUARE CHECKBOX OVERRIDE:
              "& .ag-checkbox-input-wrapper": {
                borderRadius: "4px !important",
              },
              "& .ag-checkbox-input-wrapper.ag-checked": {
                borderRadius: "4px !important",
              },
              "& .ag-checkbox-input-wrapper.ag-indeterminate": {
                borderRadius: "4px !important",
              },
              "& .ag-selection-checkbox": {
                borderRadius: "4px !important",
              },
              // Hide header checkbox completely (no header checkbox as requested)
              "& .ag-header-cell.epd-checkbox-header .ag-header-select-all": {
                display: "none !important",
              },
              "& .ag-header-cell.epd-checkbox-header .ag-checkbox-input-wrapper": {
                display: "none !important",
              },
              "& .ag-header-cell.epd-checkbox-header .ag-header-cell-text": {
                display: "none !important",
              },
              "& .ag-header-cell.epd-checkbox-header .ag-header-cell-label": {
                display: "none !important",
              },
              "& .ag-header-cell.epd-checkbox-header": {
                paddingLeft: "24px !important",
                paddingRight: "8px !important",
              },
              "& .ag-header-cell.epd-checkbox-header .ag-header-cell-comp-wrapper": {
                width: "100% !important",
                padding: "0 !important",
              },
              // Row Checkbox with 24px left breathing space from left border
              "& .ag-cell.epd-checkbox-cell": {
                paddingLeft: "24px !important",
                paddingRight: "8px !important",
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "flex-start !important",
              },
              "& .ag-cell.epd-checkbox-cell .ag-cell-wrapper": {
                width: "100% !important",
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "flex-start !important",
              },
              "& .ag-cell.epd-checkbox-cell .ag-selection-checkbox": {
                margin: "0 !important",
                display: "flex !important",
                alignItems: "center !important",
                justifyContent: "center !important",
              },
              // Resource Name: Header & Data Cell Strict Left Alignment
              "& .ag-header-cell.epd-resource-header": {
                justifyContent: "flex-start !important",
                textAlign: "left !important",
                paddingLeft: "16px !important",
              },
              "& .ag-header-cell.epd-resource-header .ag-header-cell-comp-wrapper": {
                justifyContent: "flex-start !important",
              },
              "& .ag-header-cell.epd-resource-header .ag-header-cell-label": {
                justifyContent: "flex-start !important",
                textAlign: "left !important",
              },
              "& .ag-cell.epd-resource-cell": {
                justifyContent: "flex-start !important",
                textAlign: "left !important",
                paddingLeft: "16px !important",
              },
              // Supplier / Manufacturer: Header & Data Cell Strict Left Alignment
              "& .ag-header-cell.epd-supplier-header": {
                justifyContent: "flex-start !important",
                textAlign: "left !important",
                paddingLeft: "16px !important",
              },
              "& .ag-header-cell.epd-supplier-header .ag-header-cell-comp-wrapper": {
                justifyContent: "flex-start !important",
              },
              "& .ag-header-cell.epd-supplier-header .ag-header-cell-label": {
                justifyContent: "flex-start !important",
                textAlign: "left !important",
              },
              "& .ag-cell.epd-supplier-cell": {
                justifyContent: "flex-start !important",
                textAlign: "left !important",
                paddingLeft: "16px !important",
              },
            }}
          />
        )}
      </Box>

      {/* ── FOOTER STATUS BAR ── */}
      <Box
        sx={{
          px: 3,
          py: 1.5,
          bgcolor: tok.bg.card,
          borderTop: `1px solid ${tok.border.divider || (isDark ? "#52525b" : "#e2e8f0")}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
          {Boolean(searchTerm && searchTerm.trim().length > 0) && (
            <Typography
              sx={{
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "13px",
                color: isDark ? "#cbd5e1" : "#334155",
              }}
            >
              Matching resource(s):{" "}
              <strong style={{ color: isDark ? "#ffffff" : "#0f172a" }}>
                {filteredResources.length}
              </strong>
            </Typography>
          )}

          {selectedGridRows.length > 0 && (
            <Typography
              sx={{
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "13px",
                fontWeight: 600,
                color: tok.bg.tabActive || "#659d33",
              }}
            >
              {selectedGridRows.length} resource(s) selected
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            sx={{
              textTransform: "none",
              fontFamily: ORG_PALETTE.font.family,
              fontSize: "12px",
              borderRadius: "6px",
              color: isDark ? "#cbd5e1" : "#334155",
              borderColor: isDark ? "rgba(255,255,255,0.15)" : "#cbd5e1",
            }}
          >
            Close
          </Button>
          {selectedGridRows.length > 0 && (
            <Button
              variant="contained"
              size="small"
              onClick={handleAddSelectedBatch}
              sx={{
                textTransform: "none",
                fontFamily: ORG_PALETTE.font.family,
                fontSize: "12px",
                fontWeight: 600,
                borderRadius: "6px",
                bgcolor: tok.bg.tabActive || "#659d33",
                color: "#ffffff",
                "&:hover": { bgcolor: tok.bg.tabActive || "#659d33", opacity: 0.9 },
              }}
            >
              Add Selected ({selectedGridRows.length})
            </Button>
          )}
        </Box>
      </Box>


      {/* ── CREATE USER DEFINED MATERIAL MODAL ── */}
      <Dialog
        open={isCreatingUserDefined}
        onClose={() => setIsCreatingUserDefined(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            bgcolor: tok.bg.card || (isDark ? "#18181b" : "#ffffff"),
            p: 1,
            border: `1px solid ${tok.border.card || (isDark ? "#52525b" : "#c8d4e0")}`,
          },
        }}
      >
        <Box sx={{ p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontFamily: ORG_PALETTE.font.family, fontWeight: 700, fontSize: "16px", color: isDark ? "#ffffff" : "#0f172a" }}>
            Create Custom User-Defined Material
          </Typography>
          <CloseButton onClick={() => setIsCreatingUserDefined(false)} />
        </Box>
        <Box sx={{ px: 3, pb: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Material Name / Description"
            fullWidth
            size="small"
            value={newUserMaterial.description}
            onChange={(e) => setNewUserMaterial({ ...newUserMaterial, description: e.target.value })}
            placeholder="e.g. Custom Lightweight Timber Stud"
            required
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Category"
              size="small"
              value={newUserMaterial.category}
              onChange={(e) => setNewUserMaterial({ ...newUserMaterial, category: e.target.value })}
            />
            <TextField
              label="Sub Category"
              size="small"
              value={newUserMaterial.sub_category}
              onChange={(e) => setNewUserMaterial({ ...newUserMaterial, sub_category: e.target.value })}
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Unit"
              size="small"
              value={newUserMaterial.unit}
              onChange={(e) => setNewUserMaterial({ ...newUserMaterial, unit: e.target.value })}
              placeholder="e.g. kg, m3, piece"
            />
            <TextField
              label="GWP (kg CO₂e per unit)"
              size="small"
              type="number"
              inputProps={{ step: "any" }}
              value={newUserMaterial.gwp_per_unit}
              onChange={(e) => setNewUserMaterial({ ...newUserMaterial, gwp_per_unit: e.target.value })}
              placeholder="e.g. 1.25"
            />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              label="Biogenic GWP (kg CO₂e) - Optional"
              size="small"
              type="number"
              inputProps={{ step: "any" }}
              value={newUserMaterial.biogenic_gwp_per_unit}
              onChange={(e) => setNewUserMaterial({ ...newUserMaterial, biogenic_gwp_per_unit: e.target.value })}
              placeholder="e.g. -0.5"
            />
            <TextField
              label="Density (kg/m³) - Optional"
              size="small"
              type="number"
              inputProps={{ step: "any" }}
              value={newUserMaterial.density_kg_m3}
              onChange={(e) => setNewUserMaterial({ ...newUserMaterial, density_kg_m3: e.target.value })}
              placeholder="e.g. 500"
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleCreateUserDefined}
            disabled={!newUserMaterial.description.trim()}
            sx={{
              mt: 1,
              py: 1,
              textTransform: "none",
              fontFamily: ORG_PALETTE.font.family,
              fontWeight: 600,
              bgcolor: tok.bg.tabActive || "#659d33",
              color: "#ffffff",
              borderRadius: "8px",
            }}
          >
            Save & Add to Workspace
          </Button>
        </Box>
      </Dialog>
    </Dialog>
  );
}
